/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, ObservableMap, computed, toJS, flow } from 'mobx';
import {
    generateDetail,
    generateList,
    generateListRefresh,
    generateCreate,
    generateUpdate,
    generateListMore,
    generateDelete,
    isError,
} from './actionCreator';
import profileStore from './profileStore';
import map from 'lodash/map';
import get from 'lodash/get';
import concat from 'lodash/concat';
import ErrorsStore from './errorsStore';
import fetch from '../fetch';
import _ from '../translations';

class UsersStore {
    @observable collection = new ObservableMap();
    @observable details = new ObservableMap();
    @observable templates = [];
    @observable searching = false;
    @observable usersSearch = {};

    constructor() {
        this.baseUrl = '/api/core/users';
    }

    @action
    reset = () => {
        this.collection.clear();
        this.details.clear();
        this.templates = [];
    };

    @action.bound
    detail = flow(function*({ _id = false } = {}) {
        return yield generateDetail({
            _id,
            url: `${this.baseUrl}/${_id}`,
            store: this,
            customError: _('There was a problem fetching users'),
        })();
    });

    @action.bound
    listTemplates = flow(function*({ hash, params } = {}) {
        try {
            const list = yield fetch.get({
                url: '/api/core/settings/rbac/templates',
                params: {},
            });
            if (list.error) {
                ErrorsStore.addError(list, _('There was an error loading user permissions'));
            } else {
                this.templates = get(list, 'items', []);
            }
        } catch (e) {
            ErrorsStore.addError(e, _('There was an error loading user permissions'));
            console.warn(e);
        }
    });

    @action.bound
    list = flow(function*({ hash, params } = {}) {
        return yield generateList({
            hash,
            params,
            url: `${this.baseUrl}/list/${profileStore.client._id}`,
            store: this,
            customError: _('There was a problem listing users'),
        })();
    });

    @action.bound
    listRefresh = flow(function*({ hash, params = {}, maxPagesToRefresh } = {}) {
        return yield generateListRefresh({
            hash,
            params,
            url: `${this.baseUrl}/list/${profileStore.client._id}`,
            store: this,
            customError: _('There was a problem listing users'),
            maxPagesToRefresh,
        })();
    });

    @action.bound
    create = flow(function*({ item = {} } = {}) {
        return yield generateCreate({
            item,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem creating user'),
        })();
    });

    @action.bound
    listMore = flow(function*({ hash, params = {} } = {}) {
        return yield generateListMore({
            hash,
            params,
            url: `${this.baseUrl}/list/${profileStore.client._id}`,
            store: this,
            customError: _('There was a problem listing users'),
        })();
    });

    @action.bound
    changePassword = flow(function*(data) {
        const url = '/api/auth/password/change/force/';
        try {
            const res = yield fetch.post({
                url,
                body: { data },
            });
            if (isError(res)) {
                ErrorsStore.addError(res, 'There was a problem changing password');
            }
            return res;
        } catch (e) {
            ErrorsStore.addError(e, 'There was a problem changing password');
            console.warn(e);
            return e;
        }
    });

    @action.bound
    update = flow(function*({ items, item = {} } = {}) {
        return yield generateUpdate({
            items,
            item,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem updating user'),
        })();
    });

    delete = flow(function*({ items, _id = false, type = 'logical' } = {}) {
        return yield generateDelete({
            items,
            _id,
            type,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem deleting user'),
        })();
    });

    @computed
    get userTemplatesForSelect() {
        const items = map(toJS(this.templates), template => ({
            text: template.template,
            value: template.template,
        }));
        return items;
    }

    @action.bound
    getReporting = flow(function*() {
        try {
            const report = yield fetch.get({
                url: '/api/reporting/all',
                params: {},
            });
            if (report.error) {
                ErrorsStore.addError(report, _('There was an error loading user permissions'));
            } else {
                if (report.statusCode === 204) {
                    this.report = false;
                } else {
                    this.report = get(report, 'location', false);
                }
            }
        } catch (e) {
            ErrorsStore.addError(e, _('There was an error loading user permissions'));
            console.warn(e);
        }
    });

    @action.bound
    searchList = flow(function*({ term = '', itemsPerPage = 10 } = {}) {
        this.searching = true;
        let response;
        try {
            response = yield fetch.post({
                url: '/api/search/search/users/',
                body: {
                    term,
                    itemsPerPage,
                },
            });
            this.usersSearch[term] = response;
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem in search'));
        }
        this.searching = false;
        return response;
    });

    @action.bound
    searchListMore = flow(function*({ term = '', itemsPerPage = 10 } = {}) {
        this.searching = true;

        let list = toJS(this.usersSearch[term]);

        let pagination = get(list, `pagination`, false);
        let page = 1;

        if (!pagination || pagination.first === pagination.last || pagination.page === pagination.last) return false;
        page = pagination.page + 1;

        let response;
        try {
            response = yield fetch.post({
                url: `/api/search/search/users?page=${page}`,
                body: {
                    term,
                    itemsPerPage,
                },
            });
            list.items = concat(toJS(list.items), response.items);
            list.pagination = response.pagination;
            this.usersSearch[term] = list;
        } catch (e) {
            ErrorsStore.addError(e, _('There was a problem in search'));
        }
        this.searching = false;
        return response;
    });
}

export default new UsersStore();
